import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  broadcastShape,
  channelShape,
} from 'reducers/epg';
import { useInterval } from 'utils/hooks';
import { analyticsShape } from 'components/Tracking';
import Broadcasts from './Broadcasts';
import ChannelList, { ROW_HEIGHT, OFFSET_TOP, OFFSET_BOTTOM, CHANNEL_SIZE } from './ChannelList';

const getDisabledChannels = (channels = [], broadcasts = []) => {
  const disabled = [];
  const currentTime = Math.floor(Date.now() / 1000);

  channels.forEach((ch) => {
    const isLiveAvailable = broadcasts
      .find(br => br.channelId === ch.id
        && br.start <= currentTime
        && br.stop >= currentTime)?.isLiveAvailable ?? true;

    if (!isLiveAvailable) disabled.push(ch.id);
  });

  return disabled;
};

const RECALCULATE_DISABLED_INTERVAL = 60 * 1000;

const getTotalContentHeight = channelCount => (
  OFFSET_TOP + (channelCount * ROW_HEIGHT) - CHANNEL_SIZE.gap + OFFSET_BOTTOM
);

// Time window for restoring vertical scroll after mount/update
const RESTORE_WINDOW_INITIAL_MS = 2000;
const RESTORE_WINDOW_ON_UPDATE_MS = 3000;

export default function Timeline({
  channels,
  broadcasts,
  currentTime,
  scrollLeft,
  onScrollLeftUpdate,
  scrollBarWidth,
  startTime,
  analytics,
  width,
  height,
  isRTL,
  onScrollTopUpdate,
  initialScrollTop,
}) {
  const channelListRef = useRef();
  const [scrollTop, setScrollTop] = useState(0);
  const [scrollTopMax, setScrollTopMax] = useState(0);
  const [disabledChannels, setDisabledChannels] = useState([]);
  // Restoration lifecycle to avoid emitting premature scroll updates
  const didRequestRestoreRef = useRef(false);
  const didConfirmRestoreRef = useRef(false);
  const intendedScrollTopRef = useRef(null);
  const savedInitialScrollTopRef = useRef(typeof initialScrollTop === 'number' ? initialScrollTop : 0);
  const allowRestoreUntilRef = useRef(Date.now() + RESTORE_WINDOW_INITIAL_MS);
  const userInteractedRef = useRef(false);

  // Track changes to initialScrollTop and extend restore window
  useEffect(() => {
    if (typeof initialScrollTop === 'number') {
      savedInitialScrollTopRef.current = initialScrollTop;
      allowRestoreUntilRef.current = Date.now() + RESTORE_WINDOW_ON_UPDATE_MS; // extend window a bit to allow late data
      // reset restoration flags so we can re-attempt against new content height
      didConfirmRestoreRef.current = false;
      didRequestRestoreRef.current = false;
      intendedScrollTopRef.current = null;
    }
  }, [initialScrollTop]);

  // Initialize vertical scroll from parent when data & layout are ready.
  // Uses savedInitialScrollTopRef so late data can expand the target.
  useEffect(() => {
    if (
      channels && channels.length
      && height > 0
      && !didConfirmRestoreRef.current
      && Date.now() <= allowRestoreUntilRef.current
    ) {
      // Compute max possible scroll given current channel count and viewport height
      const totalContentHeight = getTotalContentHeight(channels.length);
      const maxPossibleScroll = Math.max(0, totalContentHeight - height);
      const target = Math.max(0, Math.min(savedInitialScrollTopRef.current, maxPossibleScroll));

      intendedScrollTopRef.current = target;
      setScrollTop(target);
      didRequestRestoreRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channels, height]);

  // Re-attempt restoration while content grows within the allowed window and before user interaction
  useEffect(() => {
    if (!channels || !channels.length || height <= 0) return;
    if (userInteractedRef.current) return;
    if (Date.now() > allowRestoreUntilRef.current) return;

    const saved = savedInitialScrollTopRef.current;
    if (typeof saved !== 'number') return;

    const totalContentHeight = getTotalContentHeight(channels.length);
    const maxPossibleScroll = Math.max(0, totalContentHeight - height);
    const target = Math.max(0, Math.min(saved, maxPossibleScroll));

    if (target !== scrollTop) {
      intendedScrollTopRef.current = target;
      setScrollTop(target);
      didRequestRestoreRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channels, height, scrollTop]);

  // Keep ChannelList vertical position in sync with current scrollTop
  useEffect(() => {
    channelListRef.current?.setScrollTop(scrollTop);
  }, [scrollTop]);

  useEffect(() => {
    setDisabledChannels(getDisabledChannels(channels, broadcasts));
  }, []);

  useInterval(() => {
    setDisabledChannels(getDisabledChannels(channels, broadcasts));
  }, RECALCULATE_DISABLED_INTERVAL);

  const onScroll = (e) => {
    // If we are restoring and Collection reports a different value than intended, keep enforcing target once
    if (didRequestRestoreRef.current && !didConfirmRestoreRef.current && typeof intendedScrollTopRef.current === 'number') {
      if (e.scrollTop !== intendedScrollTopRef.current) {
        // enforce the intended position until Collection accepts it
        setScrollTop(intendedScrollTopRef.current);
        channelListRef.current?.setScrollTop(intendedScrollTopRef.current);
        return;
      }
      // Restoration confirmed
      didConfirmRestoreRef.current = true;
      intendedScrollTopRef.current = null;
    }

    setScrollTop(e.scrollTop);
    setScrollTopMax(e.scrollHeight - e.clientHeight + scrollBarWidth);

    channelListRef.current?.setScrollTop(e.scrollTop);

    if (typeof onScrollTopUpdate === 'function') {
      onScrollTopUpdate(e.scrollTop);
    }

    if (scrollLeft !== e.scrollLeft) {
      onScrollLeftUpdate(e.scrollLeft);
    }
  };

  const onWheel = (deltaY) => {
    // User interaction cancels restoration flow
    didRequestRestoreRef.current = false;
    didConfirmRestoreRef.current = true;
    intendedScrollTopRef.current = null;
    userInteractedRef.current = true;

    const newValue = scrollTop + deltaY;
    const normalizedValue = deltaY > 0 ? Math.min(newValue, scrollTopMax) : Math.max(newValue, 0);
    setScrollTop(normalizedValue);
    if (typeof onScrollTopUpdate === 'function') {
      onScrollTopUpdate(normalizedValue);
    }
  };

  return (
    <>
      <Broadcasts
        startTime={startTime}
        currentTime={currentTime}
        channels={channels}
        broadcasts={broadcasts}
        scrollTop={scrollTop}
        scrollLeft={scrollLeft}
        onScroll={onScroll}
        height={height}
        width={width}
        isRTL={isRTL}
        analytics={analytics}
      />

      <ChannelList
        ref={channelListRef}
        channels={channels}
        onWheel={onWheel}
        disabled={disabledChannels}
      />
    </>
  );
}

Timeline.propTypes = {
  broadcasts: PropTypes.arrayOf(broadcastShape).isRequired,
  channels: PropTypes.arrayOf(channelShape).isRequired,
  startTime: PropTypes.number.isRequired,
  currentTime: PropTypes.number.isRequired,
  scrollLeft: PropTypes.number,
  scrollBarWidth: PropTypes.number.isRequired,
  onScrollLeftUpdate: PropTypes.func.isRequired,
  isRTL: PropTypes.bool,
  analytics: analyticsShape.isRequired,
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  onScrollTopUpdate: PropTypes.func,
  initialScrollTop: PropTypes.number,
};
